import { useLayoutEffect } from 'react'

/**
 * Locks background scroll while `active` is true.
 *
 * Uses position:fixed on body — the only technique that reliably prevents
 * background scroll on iOS Safari. overflow:hidden alone is silently ignored
 * by iOS when a fixed-position overlay is present (known long-standing iOS bug).
 *
 * top:-scrollY preserves visual position so background content does not jump
 * when the lock is applied or released. scrollTo restores the position on unlock.
 *
 * Module-level lock count ensures overlapping overlays are safe: the first
 * caller captures the original body state; the last caller restores it.
 * Intermediate unlocks (e.g. overlay A closes while overlay B is still open)
 * decrement the count without touching the body, so B keeps the page locked.
 *
 * Pass `instant: true` to override a global `scroll-behavior: smooth` rule on
 * unlock — required for item-detail modals where the smooth animation is visible.
 */
let lockCount = 0
let savedScrollY = 0
let savedPosition = ''
let savedTop = ''
let savedWidth = ''

export function useScrollLock(active: boolean, options?: { instant?: boolean }) {
	const instant = options?.instant ?? false

	useLayoutEffect(() => {
		if (!active) return

		if (lockCount === 0) {
			savedScrollY = window.scrollY
			savedPosition = document.body.style.position
			savedTop = document.body.style.top
			savedWidth = document.body.style.width
			// CONTRACT: useScrollCompact reads body.style.position === 'fixed' to
			// detect an active scroll lock and ignore spurious scroll events (STO-3820).
			// If this mechanism ever changes, update that guard in useScrollCompact.ts.
			document.body.style.position = 'fixed'
			document.body.style.top = `-${savedScrollY}px`
			document.body.style.width = '100%'
		}
		lockCount++

		return () => {
			lockCount--
			if (lockCount === 0) {
				document.body.style.position = savedPosition
				document.body.style.top = savedTop
				document.body.style.width = savedWidth
				// Notify listeners (e.g. CategoryNav's IntersectionObserver suppression) that a
				// programmatic scroll restoration is about to fire so they can suppress spurious
				// scroll/intersection events during the transition (STO-3781).
				window.dispatchEvent(new CustomEvent('scrolllock:restore'))
				if (instant) {
					window.scrollTo({ top: savedScrollY, behavior: 'instant' })
				} else {
					window.scrollTo(0, savedScrollY)
				}
			}
		}
	}, [active, instant])
}
